/**
 * CoachingSessionInfoPanel
 * Left panel of the coaching booking modal showing coaching session details
 */

import { Banknote, Clock, Globe, MapPin } from "lucide-react"
import { getAssetUrl } from "@/utils/assets"
import { formatPrice } from "../utils/format"

interface CoachingSessionInfoPanelProps {
  coverImage: string | null
  title: string
  description?: string
  format: "IN_PERSON" | "VIRTUAL" | "HYBRID"
  duration: number
  durationType: "minutes" | "hours" | "days"
  coachName: string | null
  coachUsername: string | null
  coachAvatar: string | null
  price: number | null
  participationFee: "Free" | "Paid"
  websiteLink: string | null
  location: string | null
}

const FORMAT_LABELS = {
  IN_PERSON: "In Person",
  VIRTUAL: "Virtual",
  HYBRID: "Hybrid",
}

export function CoachingSessionInfoPanel({
  coverImage,
  title,
  description,
  format,
  duration,
  durationType,
  coachName,
  coachUsername,
  coachAvatar,
  price,
  participationFee,
  websiteLink,
  location,
}: CoachingSessionInfoPanelProps) {
  const formatDuration = (value: number, type: string) => {
    if (type === "minutes") {
      if (value >= 60) {
        const h = Math.floor(value / 60)
        const m = value % 60
        return m > 0 ? `${h}h ${m}min` : `${h}h`
      }
      return `${value} min`
    }
    if (type === "hours") {
      return `${value}h`
    }
    return `${value} day${value > 1 ? "s" : ""}`
  }
  const durationString = formatDuration(duration, durationType)

  // Determine price display
  const displayPrice = participationFee === "Free" ? 0 : price
  const displayCurrency = "USD" // Default currency, can be made configurable

  return (
    <div className="flex flex-col p-7.5 lg:w-[535px] mt-10 lg:mt-0">
      {/* Cover Image */}
      {coverImage && (
        <div className="relative w-full max-h-[320px] mb-7.5 overflow-hidden rounded-[20px]">
          <img src={coverImage} alt={title} className="h-auto w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-my-rituality-bg/75" />
        </div>
      )}

      {/* Title */}
      <h2 className="text-[24px] font-semibold leading-normal text-[#F2F0EBE6] my-5">{title}</h2>

      {/* Description */}
      {description && (
        <p className="text-[14px] leading-[25px] text-[#F2F0EBE6] my-5">{description}</p>
      )}

      {/* Coach */}
      <div className="flex items-center gap-3.75 pb-5">
        {coachAvatar ? (
          <img
            src={coachAvatar}
            alt={coachName || "Coach"}
            className="size-[60px] rounded-full object-cover"
          />
        ) : (
          <div className="flex size-[60px] items-center justify-center rounded-full bg-connect-glass-bg text-connect-cream/50">
            {(coachName || "?")[0]}
          </div>
        )}
        <div>
          <p className="text-[16px] leading-normal font-semibold text-[#F2F0EB]">
            {coachName || "Coach"}
          </p>
          {coachUsername && (
            <p className="text-[13px] leading-5.5 text-[#F2F0EB80]">@{coachUsername}</p>
          )}
        </div>
      </div>

      {/* Metadata */}
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2.5 text-sm leading-6 text-[#F2F0EB]">
          <Clock className="size-6 shrink-0" />
          <span>{durationString}</span>
        </div>

        {location && (
          <div className="flex items-center gap-2.5 text-sm leading-6 text-[#F2F0EB]">
            <MapPin className="size-6 shrink-0" />
            <span>{location}</span>
          </div>
        )}

        <div className="flex items-center gap-2.5 text-sm leading-6 text-[#F2F0EB]">
          <img src={getAssetUrl("user.svg")} alt="Format" className="size-6 shrink-0" />
          <span>{FORMAT_LABELS[format]}</span>
        </div>

        {websiteLink && (
          <div className="flex items-center gap-2.5 text-sm leading-6 text-[#F2F0EB]">
            <Globe className="size-6 shrink-0" />
            <a href={websiteLink} target="_blank" rel="noopener noreferrer" className="underline">
              {websiteLink}
            </a>
          </div>
        )}

        <div
          className={`flex items-center gap-2.5 text-sm leading-6 font-semibold ${participationFee === "Free" ? "text-brand-olive" : "text-brand-terracotta"}`}
        >
          <Banknote className="size-6 shrink-0" />
          <span>{formatPrice(displayPrice, displayCurrency)}</span>
        </div>
      </div>
    </div>
  )
}
